import type { PropTypes } from './UpdateUsernameModal.types';
import type { Cancellable } from '../../../types';
import type { ChangeEventHandler, ReactElement } from 'react';

import { Button, Flex, Text, TextField } from '@rango-dev/whale';
import { OctagonAlert } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z as zod } from 'zod';

import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { resetUpdateUsernameError, updateUsername } from '../../../state/ProfileSlice';
import { sanitize } from '../../../utils/generalFunctions/common';
import { Dialog } from '../../Dialog';
import { Avatar } from '../Avatar';

import styles from './UpdateUsernameModal.module.css';

const AVATAR_SIZE = '5';
const USERNAME_MAX_LENGTH = 15;
const USERNAME_MIN_LENGTH = 5;
const ERROR_ICON_SIZE = 12;

export function UpdateUsernameModal(props: PropTypes): ReactElement {
  const { open, onClose, onUpdate } = props;
  const { data: profileSummary } = useAppSelector((state) => state.profile.profileSummary.private);
  const { loading, error: requestError } = useAppSelector((state) => state.profile.updateUsername);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const currentUsername =
    !profileSummary || profileSummary.primaryWallet === profileSummary.username
      ? ''
      : sanitize(profileSummary.username);

  const [username, setUsername] = useState<string>(currentUsername);
  const updateUsernamePromise = useRef<Cancellable | null>(null);

  const usernameSchema = zod
    .string()
    .superRefine((value, ctx) => {
      if (value.trim() === '') {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: t('Enter a username to save changes.'),
          fatal: true,
        });
        return zod.NEVER;
      }
    })
    .pipe(
      zod
        .string()
        .min(USERNAME_MIN_LENGTH, t('Your username must be longer than 5 characters.'))
        .max(USERNAME_MAX_LENGTH, t('Your username must be shorter than 15 characters.'))
        .regex(/^[a-zA-Z0-9_]+$/, t('Username can only contain letters, numbers and (_).')),
    );

  const handleUsernameChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (requestError) {
      dispatch(resetUpdateUsernameError());
    }
    if (validationErrors.length) {
      setValidationErrors([]);
    }

    setUsername(event.target.value);
  };

  const onUpdateUsername = () => {
    navigate(`/profile/${username}`, { replace: true });
    onUpdate();
    onClose();
  };

  const handleSaveChanges = () => {
    if (requestError) {
      dispatch(resetUpdateUsernameError());
    }

    try {
      usernameSchema.parse(username);
    } catch (error) {
      if (error instanceof zod.ZodError) {
        const errorMessages = error.errors.map((issue) => issue.message);
        setValidationErrors(errorMessages);
        return;
      }
    }
    if (profileSummary?.username === username) {
      onClose();
      return;
    }
    updateUsernamePromise.current = dispatch(
      updateUsername({ userName: sanitize(username), callback: onUpdateUsername }),
    );
  };

  const resetState = () => {
    setValidationErrors([]);
    setUsername(currentUsername);
    if (requestError) {
      dispatch(resetUpdateUsernameError());
    }
  };

  useEffect(() => {
    return () => updateUsernamePromise.current?.abort();
  }, []);

  const errorMessages: string[] = requestError ? [requestError] : validationErrors;
  const hasError = errorMessages.length > 0;

  const primaryWalletDisplay = profileSummary?.primaryWallet ?? '';

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          resetState();
          onClose();
        }
      }}
      width="large"
      idPrefix="profile-page-update-username-modal"
      title={t('Edit your username')}
      description={t('Your profile picture is uniquely generated from your username.')}
      footer={
        <Button
          id="profile-page-update-username-modal-save-changes-btn"
          size={{ initial: '3', sm: '2' }}
          variant="solid"
          color="cyan"
          radius="full"
          loading={loading}
          disabled={loading}
          onClick={handleSaveChanges}
        >
          {t('Save changes')}
        </Button>
      }
    >
      <Flex direction="column" gap="5" align="center">
        {profileSummary && (
          <Avatar
            size={AVATAR_SIZE}
            username={username !== '' ? username : profileSummary.username}
          />
        )}

        {profileSummary && (
          <Flex direction="column" gap="2" width="100%">
            <Text size="3" weight="medium" as="label" htmlFor="primary-wallet-input" align="left">
              {t('Primary wallet')}
            </Text>
            <TextField.Root
              id="primary-wallet-input"
              size="3"
              readOnly
              value={primaryWalletDisplay}
            />
          </Flex>
        )}

        {profileSummary && (
          <Flex direction="column" gap="1" width="100%">
            <Flex direction="column" gap="2" width="100%">
              <Text
                size="3"
                weight="medium"
                as="label"
                htmlFor="change-username-input"
                align="left"
              >
                {t('Username')}
              </Text>
              <TextField.Root
                id="change-username-input"
                size="3"
                autoComplete="off"
                disabled={loading}
                maxLength={USERNAME_MAX_LENGTH}
                value={username}
                onChange={handleUsernameChange}
                className={hasError ? styles.errorField : undefined}
              />
            </Flex>
            {errorMessages.map((message) => (
              <Flex key={message} align="center" gap="1" className={styles.errorMessages}>
                <OctagonAlert size={ERROR_ICON_SIZE} />
                <Text size="1">{message}</Text>
              </Flex>
            ))}
          </Flex>
        )}

        <Text
          size="2"
          weight="medium"
          color="gray"
          as="div"
          className={styles.rulesText}
          align="left"
        >
          <p>{t('Username must be:')}</p>
          <ul>
            <li>{t('5-15 characters')}</li>
            <li>{t('Letters, numbers and underscore (_) only')}</li>
            <li>
              {t(
                'Inappropriate usernames, such as those referring to other websites or unconventional terms, are not allowed.',
              )}
            </li>
          </ul>
        </Text>
      </Flex>
    </Dialog>
  );
}
